import { useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { AgGridReact } from 'ag-grid-react';
import { CellKeyDownEvent, GetRowIdParams, RowClickedEvent } from 'ag-grid-community';
import { ICellRendererParams } from '@ag-grid-community/core';

import { BodySmall, Box, ModalHeader, Modal, Button, Tooltip, ErrorIcon, AgGridTable } from '@parspec/pixel';

import { useReuseDatasheetMutation } from '../queries';
import { ReuseDialogType, ReuseDialogTypeLabel } from './utils';
import { useTrackIntercomEvent } from '../../shared/hooks/useIntercomHook';
import { CustomTableHeader } from 'src/features/shared/CustomTableHeader';


type ReuseDatasheetDialogProps = {
    open: boolean;
    handleDialog: () => void;
    confirmAction: () => void;
    tableData: any;
    reuseType: ReuseDialogType;
    selectedRowIds: Array<number | string>;
};

const ReuseDatasheetDialog = (props: ReuseDatasheetDialogProps) => {
    const { open, handleDialog, confirmAction, tableData, reuseType, selectedRowIds } = props;
    const { bomId } = useParams();
    const { trackIntercomEvent } = useTrackIntercomEvent();

    const tableRef = useRef<AgGridReact | null>(null);

    const [selectedRowForPayload, setSelectedRowForPayload] = useState<any[]>([]);
    const [reuseTypeClicked, setReuseTypeClicked] = useState<string>('');
    const { isLoading, mutateAsync: confirmReuse } = useReuseDatasheetMutation();
    const documentAnnotatedStatus = ['CONFIRMED', 'IN_PROGRESS'];
    const documentType = reuseType;

    const { updatedTableData, needConfirmationDialog } = useMemo(() => {
        let needConfirmationDialog = false;

        const updatedTableData = tableData.filter((item: any) => {
            if (selectedRowIds.includes(item.id)) {
                if (item[documentType]?.document_selection_status === 'CONFIRMED') {
                    needConfirmationDialog = true;
                }
                return false;  // Exclude items that are in selectedRowIds
            }

            item.is_hidden = false;  // Set is_hidden to false for non-selected items
            return item[documentType]?.document_selection_status === 'CONFIRMED';
        }).map((item: any) => ({ category: item.category?.toUpperCase() || '', manufacturer: item.manufacturer?.toUpperCase() || '', model_number: item.model_number?.toUpperCase() || '', ...item }))

        return { updatedTableData, needConfirmationDialog };
    }, [tableData, selectedRowIds, documentType]);

    const confirmReuseDatasheets = async (reuseTypeForPayload: string) => {
        let selected_document;
        if (documentType === 'datasheet') {
            selected_document = selectedRowForPayload[0].selected_datasheet_id;
        } else {
            selected_document = selectedRowForPayload[0][documentType].id;
        }
        const payload = {
            bod_id: selectedRowIds,
            selected_document_id: selected_document,
            data: reuseTypeForPayload
        };
        try {
            await confirmReuse({ bomId: bomId!, payload, documentType });
            confirmAction();
        } catch (e) {
            console.error(e);
        }
    };

    const getDatasheetContent = useCallback((props: ICellRendererParams) => {

        const { all_pages_hidden } = props.data;
        return (
            <Box display="flex" alignItems="center" justifyContent="center" ml={1} height={'27px'}>
                <Box>
                    <Button color={all_pages_hidden ? 'warning' : 'success'} startIcon={all_pages_hidden && <ErrorIcon />} size="small">
                        {all_pages_hidden ? 'No Pages' : 'Selected'}
                    </Button>
                </Box>
            </Box>
        );
    }, []);

    const getAnnotateContent = useCallback((props: ICellRendererParams) => {
        if (props?.data?.[documentType]?.annotations_status === 'IN_PROGRESS') {
            return (
                <Box display="flex" alignItems="center" justifyContent="center" ml={2} height={'27px'}>
                    <Button color="tertiary" size="small">
                        In Progress
                    </Button>
                </Box>
            );
        } else if (props?.data?.[documentType]?.annotations_status === 'CONFIRMED') {
            return (
                <Box display="flex" alignItems="center" justifyContent="center" ml={2} height={'27px'}>
                    <Button color="success" size="small">
                        Confirmed
                    </Button>
                </Box>
            );
        }
        return <></>;
    }, [documentType]);

    const handleReusedEvent = () => trackIntercomEvent('reuse_datasheet');
    const onReuseDocument = () => {
        setReuseTypeClicked('document');
        confirmReuseDatasheets('document');
        handleReusedEvent();
    };
    const onReuseAnnotation = () => {
        setReuseTypeClicked('xfdf');
        confirmReuseDatasheets('xfdf');
        handleReusedEvent();
    };
    const subTitle = <Box>Choose an existing product to reuse the documentation from. </Box>;
    const isButtonDisabled = () => {
        const row = selectedRowForPayload[0];
        return !documentAnnotatedStatus.includes(row[documentType]?.annotations_status);
    };

    const modalFooter = (
        <Box display="flex" justifyContent="space-between" alignItems={'center'}>
            <Box>
                {needConfirmationDialog && (
                    <BodySmall
                        fontWeight={700}
                        width={'529px'}
                        padding={'8px'}
                        sx={{
                            color: 'warning.main',
                            div: {
                                WebkitLineClamp: 'unset !important'
                            }
                        }}
                    >
                        One or more products from your initial selection have an associated <br />
                        document.
                        <span style={{ fontWeight: '500' }}>These existing documents and annotations will be overwritten.</span>
                    </BodySmall>
                )}
            </Box>
            <Box display={'flex'} gap={2}>
                <Button color="secondary" variant="outlined" onClick={handleDialog}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedRowForPayload.length || (isLoading && reuseTypeClicked === 'xfdf')}
                    onClick={onReuseDocument}
                    isLoading={isLoading && reuseTypeClicked === 'document'}
                >
                    Reuse Document
                </Button>
                <Tooltip title={selectedRowForPayload.length && isButtonDisabled() ? 'No annotations confirmed for this product' : ''} placement={'top'}>
                    <span>
                        <Button
                            variant="contained"
                            disabled={!selectedRowForPayload.length || isButtonDisabled() || (isLoading && reuseTypeClicked === 'document')}
                            onClick={onReuseAnnotation}
                            isLoading={isLoading && reuseTypeClicked === 'xfdf'}
                        >
                            Reuse Document & Annotations
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );

    const columnDefs = [
        { field: 'id', hide: true },
        {
            field: 'category',
            headerName: "Type",
            minWidth: 200,
            sortable: true,
            headerComponent: CustomTableHeader,
            headerComponentParams: {
                enableSorting: true,
            },
            cellClass: 'grid-cell-centered',

        },
        {
            field: 'manufacturer',
            headerName: "Manufacturer",
            minWidth: 200,
            sortable: true,
            headerComponent: CustomTableHeader,
            headerComponentParams: {
                enableSorting: true
            },
            cellClass: 'grid-cell-centered',
        },
        {
            field: 'model_number', headerName: "Model Number", minWidth: 360, sortable: true, headerComponent: CustomTableHeader,
            headerComponentParams: {
                enableSorting: true
            }, cellClass: 'grid-cell-centered',
        },
        {
            headerName: ReuseDialogTypeLabel[reuseType], width: 140, minWidth: 140, cellRenderer: getDatasheetContent, cellClass: 'grid-cell-centered', sortable: false, field: 'all_pages_hidden', headerClass: 'ag-header-cell-label'
        },
        {
            headerName: 'Annotation', width: 123, minWidth: 123, cellRenderer: getAnnotateContent, cellClass: 'grid-cell-centered', sortable: false, field: 'annotation_status', headerClass: 'ag-header-cell-label'
        }



    ]
    const getRowId = useCallback((params: GetRowIdParams) => {
        return params?.data?.id;
    }, [])

    const onRowClicked = (params: RowClickedEvent) => {
        const id = params.data.id;
        if (selectedRowForPayload[0]?.id === id) {
            params.api.deselectAll();
            setSelectedRowForPayload([]);
        } else {
            setSelectedRowForPayload(() => [params.data]);
        }
    }
    const handleOnCellKeyDown = (params: CellKeyDownEvent) => {
        const event = params.event as KeyboardEvent;

        // If no row is selected, select the first row
        if (!params.api.getSelectedRows().length) {
            const firstRowNode = params.api.getDisplayedRowAtIndex(0);
            if (firstRowNode) {
                firstRowNode.setSelected(true);
                params.api.setFocusedCell(0, params.column.getId());
                setSelectedRowForPayload([firstRowNode.data]);
                return;
            }
        }

        // Here Handle navigation keys (arrows, enter, shift+enter)
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event?.key)) {

            const currentRowIndex = params?.rowIndex ?? 0;  // Add default value of 0
            const nextRowIndex = (event?.key === 'ArrowDown' || (event?.key === 'Enter' && !event.shiftKey))
                ? currentRowIndex + 1
                : currentRowIndex - 1;

            if (nextRowIndex >= 0 && nextRowIndex < params.api.getDisplayedRowCount()) {
                const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);
                if (nextRowNode) {
                    nextRowNode.setSelected(true);
                    params.api.setFocusedCell(nextRowIndex, params.column.getId());
                    setSelectedRowForPayload([nextRowNode.data]);
                }
            }
        }
    }

    return (
        <>
            <Modal open={open} header={<ModalHeader title={`Reuse ${ReuseDialogTypeLabel[reuseType]}`} onClose={handleDialog} children={subTitle} />} footer={modalFooter}>
                <Box height={'460px'} width={'1037px'}>
                    <AgGridTable
                        rowData={updatedTableData}
                        ref={tableRef}
                        getRowId={getRowId}
                        rowHeight={44}
                        columnDefs={columnDefs}
                        isLoading={false}
                        showToolbarPanel={false}
                        defaultColDef={{ flex: 1, sortable: false, resizable: false }}
                        gridOptions={{
                            rowSelection: 'single',
                            suppressRowDeselection: false
                        }}
                        className="hover-highlight-grid"
                        onRowClicked={onRowClicked}
                        onCellKeyDown={handleOnCellKeyDown}

                    />
                </Box>
            </Modal>
        </>
    );
};

export default ReuseDatasheetDialog;
